import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/shadcn/select';
import type { PackagingDimensionFormState } from '../types/dimensions';

interface PackagingDimensionsFieldsProps {
  state: PackagingDimensionFormState;
  onChange: (state: PackagingDimensionFormState) => void;
  disabled?: boolean;
  labels: {
    sectionTitle: string;
    foldedTitle: string;
    assembledTitle: string;
    length: string;
    width: string;
    height: string;
    unitCm: string;
    unitMt: string;
  };
}

export const PackagingDimensionsFields = ({
  state,
  onChange,
  disabled = false,
  labels,
}: PackagingDimensionsFieldsProps) => {
  const updateField = (field: keyof PackagingDimensionFormState, value: string) => {
    onChange({ ...state, [field]: value });
  };

  return (
    <div className="space-y-4 md:col-span-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-title">{labels.sectionTitle}</h3>
        <Select
          value={state.dimensionUnit}
          onValueChange={(value) => updateField('dimensionUnit', value)}
          disabled={disabled}
        >
          <SelectTrigger
            className="w-20 border-none bg-transparent px-0 py-0 shadow-none h-auto min-w-0"
            data-testid="packaging-dimension-unit-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cm">{labels.unitCm}</SelectItem>
            <SelectItem value="mt">{labels.unitMt}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-3 border border-card-border rounded-lg p-3">
          <p className="text-sm font-medium text-text">{labels.foldedTitle}</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-subtext mb-1 block">{labels.length}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={state.foldedLength}
                onChange={(e) => updateField('foldedLength', e.target.value)}
                disabled={disabled}
                data-testid="packaging-folded-length-input"
              />
            </div>
            <div>
              <Label className="text-xs text-subtext mb-1 block">{labels.width}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={state.foldedWidth}
                onChange={(e) => updateField('foldedWidth', e.target.value)}
                disabled={disabled}
                data-testid="packaging-folded-width-input"
              />
            </div>
            <div>
              <Label className="text-xs text-subtext mb-1 block">{labels.height}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={state.foldedHeight}
                onChange={(e) => updateField('foldedHeight', e.target.value)}
                disabled={disabled}
                data-testid="packaging-folded-height-input"
              />
            </div>
          </div>
        </div>

        <div className="space-y-3 border border-card-border rounded-lg p-3">
          <p className="text-sm font-medium text-text">{labels.assembledTitle}</p>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-subtext mb-1 block">{labels.length}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={state.assembledLength}
                onChange={(e) => updateField('assembledLength', e.target.value)}
                disabled={disabled}
                data-testid="packaging-assembled-length-input"
              />
            </div>
            <div>
              <Label className="text-xs text-subtext mb-1 block">{labels.width}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={state.assembledWidth}
                onChange={(e) => updateField('assembledWidth', e.target.value)}
                disabled={disabled}
                data-testid="packaging-assembled-width-input"
              />
            </div>
            <div>
              <Label className="text-xs text-subtext mb-1 block">{labels.height}</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={state.assembledHeight}
                onChange={(e) => updateField('assembledHeight', e.target.value)}
                disabled={disabled}
                data-testid="packaging-assembled-height-input"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
